import {
    ArrowDownIcon,
    ArrowUpIcon,
    PlusIcon,
    TrashIcon,
    XMarkIcon,
} from "@heroicons/react/24/outline";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useState } from "react";
import { useAdmin } from "../../contexts/AdminContext";
import { adminService } from "../../services/api";

const HotSellingManagement = () => {
    const { refreshHotSelling } = useAdmin();
    const [hotSelling, setHotSelling] = useState([]);
    const [allProducts, setAllProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");

    const fetchHotSelling = useCallback(async () => {
        try {
            setLoading(true);
            const response = await adminService.getHotSelling();
            if (response.status === "success") {
                setHotSelling(response.data.hotSelling || []);
            }
        } catch (error) {
            console.error("Failed to fetch hot selling:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchAllProducts = useCallback(async () => {
        try {
            const response = await adminService.getAllProducts({ limit: 1000 });
            if (response.status === "success") {
                setAllProducts(response.data.products || []);
            }
        } catch (error) {
            console.error("Failed to fetch products:", error);
        }
    }, []);

    useEffect(() => {
        fetchHotSelling();
        fetchAllProducts();
    }, [fetchHotSelling, fetchAllProducts]);

    const handleAddProduct = async (productId) => {
        try {
            await adminService.addHotSelling(productId);
            await refreshHotSelling();
            fetchHotSelling();
            setShowAddModal(false);
            setSearchQuery("");
        } catch (error) {
            console.error("Failed to add product:", error);
            alert(error.response?.data?.message || "Failed to add product to hot selling");
        }
    };

    const handleRemoveProduct = async (hotSellingId) => {
        if (window.confirm("Are you sure you want to remove this product from hot selling?")) {
            try {
                await adminService.removeHotSelling(hotSellingId);
                await refreshHotSelling();
                fetchHotSelling();
            } catch (error) {
                console.error("Failed to remove product:", error);
                alert(error.response?.data?.message || "Failed to remove product");
            }
        }
    };

    const handleMoveUp = async (index) => {
        if (index === 0) return;
        const newOrder = [...hotSelling];
        [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
        const orderIds = newOrder.map(hs => hs._id);
        try {
            await adminService.reorderHotSelling(orderIds);
            await refreshHotSelling();
            fetchHotSelling();
        } catch (error) {
            console.error("Failed to reorder:", error);
            alert("Failed to reorder products");
        }
    };

    const handleMoveDown = async (index) => {
        if (index === hotSelling.length - 1) return;
        const newOrder = [...hotSelling];
        [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
        const orderIds = newOrder.map(hs => hs._id);
        try {
            await adminService.reorderHotSelling(orderIds);
            await refreshHotSelling();
            fetchHotSelling();
        } catch (error) {
            console.error("Failed to reorder:", error);
            alert("Failed to reorder products");
        }
    };

    const availableProducts = allProducts.filter(product => {
        const isAlreadyAdded = hotSelling.some(hs => hs.product?._id === product._id);
        const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase());
        return !isAlreadyAdded && matchesSearch && product.isActive;
    });

    const getProductPricing = (product) => {
        if (!product.pricing || product.pricing.length === 0) return "Price on request";
        const firstPricing = product.pricing[0];
        const price = firstPricing.discountPrice || firstPricing.basePrice;
        return `${firstPricing.quantity} cards from ₹${price}`;
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-6 space-y-6"
        >
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-3xl font-bold bg-gradient-to-r from-[#008000] to-emerald-600 bg-clip-text text-transparent">
                        Hot Selling Management
                    </h2>
                    <p className="text-gray-600 mt-1">Manage products displayed in the Hot Selling section</p>
                </div>
                <motion.button
                    onClick={() => setShowAddModal(true)}
                    className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-[#008000] to-emerald-600 text-white rounded-xl hover:from-[#008000] hover:to-emerald-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                >
                    <PlusIcon className="w-5 h-5" />
                    <span>Add Product</span>
                </motion.button>
            </div>

            {loading ? (
                <div className="text-center py-12">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#008000]"></div>
                </div>
            ) : hotSelling.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-xl">
                    <p className="text-gray-500 text-lg">No products in hot selling yet</p>
                    <p className="text-gray-400 text-sm mt-2">Click "Add Product" to get started</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {hotSelling.map((hotSellingItem, index) => {
                        const product = hotSellingItem.product;
                        if (!product) return null;

                        return (
                            <motion.div
                                key={hotSellingItem._id}
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100"
                            >
                                <div className="aspect-square overflow-hidden bg-gray-50 relative">
                                    <img
                                        src={product.images?.[0]?.url || "/placeholder.svg"}
                                        alt={product.name}
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <div className="p-4">
                                    <h3 className="font-bold text-black text-lg mb-2 line-clamp-2">{product.name}</h3>
                                    <p className="text-gray-700 text-sm mb-4">{getProductPricing(product)}</p>
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="flex items-center gap-1">
                                            <motion.button
                                                onClick={() => handleMoveUp(index)}
                                                disabled={index === 0}
                                                className={`p-2 rounded-lg ${index === 0
                                                    ? "bg-gray-100 text-gray-300 cursor-not-allowed"
                                                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                                                    }`}
                                                whileHover={index !== 0 ? { scale: 1.1 } : {}}
                                                whileTap={index !== 0 ? { scale: 0.9 } : {}}
                                            >
                                                <ArrowUpIcon className="w-4 h-4" />
                                            </motion.button>
                                            <motion.button
                                                onClick={() => handleMoveDown(index)}
                                                disabled={index === hotSelling.length - 1}
                                                className={`p-2 rounded-lg ${index === hotSelling.length - 1
                                                    ? "bg-gray-100 text-gray-300 cursor-not-allowed"
                                                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                                                    }`}
                                                whileHover={index !== hotSelling.length - 1 ? { scale: 1.1 } : {}}
                                                whileTap={index !== hotSelling.length - 1 ? { scale: 0.9 } : {}}
                                            >
                                                <ArrowDownIcon className="w-4 h-4" />
                                            </motion.button>
                                        </div>
                                        <motion.button
                                            onClick={() => handleRemoveProduct(hotSellingItem._id)}
                                            className="p-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100"
                                            whileHover={{ scale: 1.1 }}
                                            whileTap={{ scale: 0.9 }}
                                        >
                                            <TrashIcon className="w-4 h-4" />
                                        </motion.button>
                                    </div>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            )}

            <AnimatePresence>
                {showAddModal && (
                    <AddProductModal
                        products={availableProducts}
                        searchQuery={searchQuery}
                        onSearchChange={setSearchQuery}
                        onAdd={handleAddProduct}
                        onClose={() => {
                            setShowAddModal(false);
                            setSearchQuery("");
                        }}
                    />
                )}
            </AnimatePresence>
        </motion.div>
    );
};

const AddProductModal = ({ products, searchQuery, onSearchChange, onAdd, onClose }) => {
    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={onClose}
        >
            <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[80vh] overflow-hidden flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                    <h3 className="text-2xl font-bold text-gray-900">Add Product to Hot Selling</h3>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-lg hover:bg-gray-100 text-gray-500"
                    >
                        <XMarkIcon className="w-6 h-6" />
                    </button>
                </div>

                <div className="p-6 border-b border-gray-200">
                    <input
                        type="text"
                        placeholder="Search products..."
                        value={searchQuery}
                        onChange={(e) => onSearchChange(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                </div>

                <div className="flex-1 overflow-y-auto p-6">
                    {products.length === 0 ? (
                        <div className="text-center py-12">
                            <p className="text-gray-500">
                                {searchQuery ? "No products found matching your search" : "No available products"}
                            </p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {products.map((product) => (
                                <motion.div
                                    key={product._id}
                                    className="border border-gray-200 rounded-lg p-4 hover:border-green-500 hover:shadow-md transition-all cursor-pointer"
                                    onClick={() => onAdd(product._id)}
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    <div className="flex items-start gap-4">
                                        <img
                                            src={product.images?.[0]?.url || "/placeholder.svg"}
                                            alt={product.name}
                                            className="w-20 h-20 object-cover rounded-lg aspect-square"
                                        />
                                        <div className="flex-1 min-w-0">
                                            <h4 className="font-semibold text-gray-900 line-clamp-2">{product.name}</h4>
                                            <p className="text-sm text-gray-500 mt-1 line-clamp-2">{product.description}</p>
                                            <p className="text-sm font-medium text-[#008000] mt-2">
                                                {product.pricing?.[0]
                                                    ? `${product.pricing[0].quantity} cards from ₹${product.pricing[0].discountPrice || product.pricing[0].basePrice}`
                                                    : "Price on request"}
                                            </p>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    )}
                </div>
            </motion.div>
        </motion.div>
    );
};

export default HotSellingManagement;

